import { Inject, Injectable, InjectionToken, OnDestroy } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { from, Observable, Subject } from 'rxjs';
import { filter, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { FormSignatureService } from '@protocols/domain';
import type { IRetrieveResourceDetailQuery } from '@protocols/use-cases';

import { AssignedResourcesState } from '../state/assigned-resources.state';
import type { ResourceContainer } from '../state/assigned-resources.state';
import { ResourceEditorState } from '../state/resource-editor.state';
import type { ResourceEditorDraft } from '../state/resource-editor.state';
import { StagedResourcesState } from '../state/staged-resources.state';
import type { ResourceDraftData, ResourceDraftOverride } from '../state/staged-resources.state';

export const RETRIEVE_RESOURCE_DETAIL_QUERY = new InjectionToken<IRetrieveResourceDetailQuery>(
  'RETRIEVE_RESOURCE_DETAIL_QUERY'
);

/**
 * One-shot result of an editing session. Payload-free on purpose: the draft the
 * consumer needs lives in the shared editor state as `draft$`, so the event
 * carries only *which action* the user took. Mirrors `PipelineEditorOutcome`.
 */
export type ResourceEditorOutcome = { kind: 'saved' } | { kind: 'cancelled' };

@Injectable()
export class ResourceEditorEffects implements OnDestroy {
  /**
   * One-shot save/cancel outcome. Event, not state — never replays to late
   * subscribers, so a reopened dialog can't consume a prior session's result.
   */
  private readonly _outcome$ = new Subject<ResourceEditorOutcome>();
  public readonly outcome$: Observable<ResourceEditorOutcome> = this._outcome$.asObservable();

  constructor(
    private readonly state: ResourceEditorState,
    private readonly assignedState: AssignedResourcesState,
    private readonly stagedState: StagedResourcesState,
    @Inject(RETRIEVE_RESOURCE_DETAIL_QUERY)
    private readonly retrieveResourceDetailQuery: IRetrieveResourceDetailQuery
  ) {}

  /**
   * Live canonical signature of the editing session. Single source for both
   * the facade's `hasChanges$` compare and `captureBaseline`'s snapshot.
   *
   * NOTE: declared above `captureBaseline` on purpose — its initializer reads
   * this property at construction time.
   */
  public readonly currentSignature$: Observable<string> = this.state.select(
    this.state.draft$,
    this.state.containerId$,
    this.assignedState.containers$,
    this.stagedState.pendingResources$,
    this.stagedState.resourceOverrides$,
    (draft, containerId, containers, pendingResources, resourceOverrides) =>
      FormSignatureService.compute(
        this.projectSignature({ draft, containerId, containers, pendingResources, resourceOverrides })
      )
  );

  public readonly loadResource = this.state.effect((resourceId$: Observable<string | null>) =>
    resourceId$.pipe(
      tap((resourceId) => {
        if (resourceId === null) {
          // Create mode: a pristine editor whose tree/ledger key is a fresh
          // placeholder. Seed that same id as the tree root so the (future)
          // nested mask manager has a children-loaded, pending-save node to
          // attach to — there is nothing in the DB to fetch.
          const placeholderId = uuidv4();
          this.state.reset();
          this.state.startCreate({ placeholderId });
          this.assignedState.seed({
            root: { kind: 'resource', id: placeholderId, name: '', pendingPersistence: 'save' },
            children: [],
          });
          this.captureBaseline();
        } else {
          this.state.setLoading(true);
          this.state.setLoadException(null);
        }
      }),
      filter((resourceId): resourceId is string => resourceId !== null),
      switchMap((resourceId) =>
        from(this.retrieveResourceDetailQuery.execute(resourceId)).pipe(
          tapResponse(
            (output) => {
              this.state.seedFromResource(output);
              // Edit mode: a clean root (no pending flag) whose children the
              // detail read already carries, so they are pre-loaded.
              this.assignedState.seed({
                root: { kind: 'resource', id: output.id, name: output.name },
                children: output.children,
              });
              this.captureBaseline();
            },
            (error: Error) => this.state.setLoadException(error)
          )
        )
      )
    )
  );

  /**
   * Dialog create-mode entry. Unlike the page flow above (which seeds the
   * placeholder as the tree ROOT), a dialog session lives inside an existing
   * tree: the placeholder is staged as a CHILD of the opener's container, so
   * the nested editor's own embedded manager has a children-loaded,
   * pending-save anchor and the opener's right list tracks the draft.
   * Deliberately asymmetric with `loadResource(null)` — do not unify them.
   */
  public readonly startCreateDraft = this.state.effect(
    (payload$: Observable<{ parentId: string; lockedType?: string }>) =>
      payload$.pipe(
        tap(({ parentId, lockedType }) => {
          const placeholderId = uuidv4();
          this.state.startCreate({ placeholderId, lockedType });
          this.assignedState.stageDraft({ parentId, draft: { id: placeholderId, name: '' } });
          // After `startCreate`, so a `lockedType` prefill is part of the baseline, not dirt.
          this.captureBaseline();
        })
      )
  );

  /**
   * Dialog edit-mode load for a resource that already exists as a node in the
   * shared tree. Seeds the editor from the detail read and back-fills the
   * node's children ONLY when they were never loaded — never `seed()` here:
   * that would replace the node, clearing its staging flags and clobbering any
   * staged `childIds`.
   */
  public readonly loadAssignedResource = this.state.effect((resourceId$: Observable<string>) =>
    resourceId$.pipe(
      tap(() => {
        this.state.setLoading(true);
        this.state.setLoadException(null);
      }),
      switchMap((resourceId) =>
        from(this.retrieveResourceDetailQuery.execute(resourceId)).pipe(
          withLatestFrom(this.assignedState.containers$),
          tapResponse(
            ([output, containers]) => {
              this.state.seedFromResource(output);
              const node = containers[output.id];
              if (node && !node.childrenLoaded) {
                this.assignedState.setChildren({ containerId: output.id, children: output.children });
              }
              this.captureBaseline();
            },
            (error: Error) => this.state.setLoadException(error)
          )
        )
      )
    )
  );

  /**
   * Snapshots the session signature into `baselineSignature` so the facade's
   * `hasChanges$` can detect divergence afterwards. Self-gating: waits until
   * the session has a container AND that container's children are known-loaded
   * (a signature computed before the lazy load would treat the load itself as
   * user work). Triggered at every seed-completion point — the two seed paths
   * living in the dialog facade are why this is public. Error paths never
   * trigger it: baseline stays `null`, `hasChanges$` stays false, cancel
   * closes silently (correct — nothing was seeded).
   */
  public readonly captureBaseline = this.state.effect<void>((trigger$) =>
    trigger$.pipe(
      switchMap(() =>
        this.state.containerId$.pipe(
          filter((containerId): containerId is string => containerId !== null),
          take(1),
          switchMap((containerId) =>
            this.assignedState.childrenLoadedOf(containerId).pipe(
              filter((loaded) => loaded),
              take(1)
            )
          )
        )
      ),
      withLatestFrom(this.currentSignature$),
      tap(([, signature]) => this.state.setBaselineSignature(signature))
    )
  );

  // ============================================================================
  // Signature projection
  // ============================================================================

  /*
   * Canonical fingerprint of everything a resource editing session can
   * change: the root draft, the root's direct child assignments, and the
   * staged ledger rows of the root's subtree. Compared against a baseline
   * captured at seed to gate the discard-confirmation warning (pipeline-step
   * pattern). Serialized by `FormSignatureService.compute` (recursive key
   * sort), so key-insertion order never flips the signature.
   *
   * Deliberate scope choices:
   *  - `draft.id` is excluded — identity, not work.
   *  - childIds deeper than the root are excluded — lazy loads (`setChildren`)
   *    mutate them without user action; a deeper saved session always stages a
   *    ledger row, which IS included.
   *  - The root's OWN ledger rows are excluded — root work is already covered
   *    by the draft, and the root row is churned by `stageFromTree` sync.
   *  - `nestedResourcesCount` / `pending` are stripped from ledger rows —
   *    tree-derived bookkeeping resynced on every tree emission; including
   *    them would flag phantom dirt. Accepted miss: a nested re-edit that only
   *    moves a pre-existing override row's `nestedResourcesCount` stays
   *    "clean" — the work still persists on save; only the warning misses it.
   */
  private projectSignature(input: {
    draft: ResourceEditorDraft;
    containerId: string | null;
    containers: Record<string, ResourceContainer>;
    pendingResources: ResourceDraftData[];
    resourceOverrides: Record<string, ResourceDraftOverride>;
  }): unknown {
    const { draft, containerId, containers, pendingResources, resourceOverrides } = input;

    const root = {
      name: draft.name,
      type: draft.type,
      version: draft.version,
      description: draft.description,
      value: draft.value,
      metadata: draft.metadata ?? {},
      displayName: this.normalizeDisplayName(draft.displayName),
      file: this.fileIdentity(draft.file),
    };

    const rootChildIds = containerId ? [...(containers[containerId]?.childIds ?? [])].sort() : [];

    const subtreeIds = this.collectSubtreeIds(containerId, containers);
    const pending = pendingResources
      .filter((row) => row.id !== containerId && subtreeIds.has(row.id))
      .map((row) => this.sanitizeLedgerRow(row))
      .sort((a, b) => a.id.localeCompare(b.id));
    const overrides = Object.keys(resourceOverrides)
      .filter((id) => id !== containerId && subtreeIds.has(id))
      .sort()
      .map((id) => ({
        original: this.sanitizeLedgerRow(resourceOverrides[id].original),
        override: this.sanitizeLedgerRow(resourceOverrides[id].override),
      }));

    return { root, rootChildIds, pending, overrides };
  }

  /* BFS over `childIds` from the root container; empty when the root is unknown. */
  private collectSubtreeIds(containerId: string | null, containers: Record<string, ResourceContainer>): Set<string> {
    const ids = new Set<string>();
    if (containerId === null) return ids;
    const queue: string[] = [containerId];
    while (queue.length > 0) {
      const id = queue.shift() as string;
      if (ids.has(id)) continue;
      ids.add(id);
      for (const childId of containers[id]?.childIds ?? []) queue.push(childId);
    }
    return ids;
  }

  /*
   * `nestedResourcesCount` / `pending` are stripped — tree-derived bookkeeping
   * resynced on every tree emission; including them would flag phantom dirt.
   */
  private sanitizeLedgerRow(row: ResourceDraftData) {
    return {
      id: row.id,
      name: row.name,
      type: row.type,
      description: row.description,
      version: row.version,
      value: row.value,
      metadata: row.metadata ?? {},
      displayName: this.normalizeDisplayName(row.displayName),
      file: this.fileIdentity(row.file),
    };
  }

  /* A picked File compares by identity fields — the object itself is not signable. */
  private fileIdentity(file: File | null | undefined): { name: string; size: number; lastModified: number } | null {
    return file ? { name: file.name, size: file.size, lastModified: file.lastModified } : null;
  }

  /**
   * Drops empty-string entries so `{}` ≡ `{en: ''}` (the facade's
   * `toFullLangMap` backfills missing languages with `''`); key sorting is the
   * service's job.
   */
  private normalizeDisplayName(displayName: Record<string, string> | undefined): Record<string, string> {
    return Object.fromEntries(Object.entries(displayName ?? {}).filter(([, value]) => value !== ''));
  }

  /** Emit the editing-session outcome as a one-shot event (see `outcome$`). */
  public readonly confirm = this.state.effect<ResourceEditorOutcome>((outcome$: Observable<ResourceEditorOutcome>) =>
    outcome$.pipe(tap((outcome: ResourceEditorOutcome) => this._outcome$.next(outcome)))
  );

  // Angular tears the effects down with the providing component injector — the
  // same moment the session state goes (both live in
  // `resourceEditorSessionProviders`), so no outcome can outlive its session.
  ngOnDestroy(): void {
    this._outcome$.complete();
  }
}
